import { useEffect, useState } from 'react'
import { AnimatePresence, motion, useReducedMotion } from 'motion/react'
import { AmbientBackground } from './components/AmbientBackground'
import { BookingGuide } from './components/BookingGuide'
import { Header } from './components/Header'
import { fallbackContent } from './data/fallback'
import { useRouter } from './lib/router'
import { AboutPage } from './pages/AboutPage'
import { HomePage } from './pages/HomePage'
import { WorkPage } from './pages/WorkPage'
import type { PortfolioContent } from './types'

const hasSupabaseConfig = Boolean(
  import.meta.env.VITE_SUPABASE_URL?.trim()
  && import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY?.trim(),
)

function App() {
  const { pathname } = useRouter()
  const [content, setContent] = useState<PortfolioContent>(fallbackContent)
  const reduceMotion = useReducedMotion()

  useEffect(() => {
    if (!hasSupabaseConfig) return

    let mounted = true
    import('./lib/supabase').then(({ loadPortfolioContent }) => loadPortfolioContent()).then((nextContent) => {
      if (mounted) setContent(nextContent)
    }).catch(() => undefined)
    return () => { mounted = false }
  }, [])

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' })
    document.title = `Angel Rodriguez | ${pathname === '/work' ? 'My Portfolio' : pathname === '/about' ? 'About Me' : 'Technology Solutions Consultant'}`
  }, [pathname])

  const page = pathname === '/work'
    ? <WorkPage projects={content.projects} />
    : pathname === '/about'
      ? <AboutPage profile={content.profile} />
      : <HomePage content={content} />

  return (
    <div className="site-shell">
      <AmbientBackground />
      <a className="skip-link" href="#main-content">Skip to content</a>
      <Header socialLinks={content.socialLinks} />
      <AnimatePresence mode="wait">
        <motion.div
          key={pathname}
          initial={reduceMotion ? { opacity: 0 } : { opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          exit={reduceMotion ? { opacity: 0 } : { opacity: 0, y: -8 }}
          transition={{ duration: reduceMotion ? 0.01 : 0.42, ease: [0.22, 1, 0.36, 1] }}
        >
          {page}
        </motion.div>
      </AnimatePresence>
      <BookingGuide />
    </div>
  )
}

export default App
